import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PhoneCall, PhoneOff, PhoneForwarded, X, ExternalLink, RotateCcw, Loader2, AlertTriangle } from 'lucide-react';
import { useT } from '../utils/translate';
import { getSocket } from '../utils/socket';
import { srv } from '../utils/serverText';
import { fmtPhone } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const last9 = (p) => String(p || '').replace(/\D/g, '').slice(-9);

const clock = (sec) => `${Math.floor(Math.max(0, sec) / 60)}:${String(Math.floor(Math.max(0, sec) % 60)).padStart(2, '0')}`;
const NO_START_MS = 20000;

// Панель «Идёт звонок» — показывается ТОЛЬКО звонящему сотруднику (по его внутреннему номеру):
//   отправили команду → ответьте в MicroSIP → разговор (таймер) → итог / причина неудачи.
// Источники: локальное событие после клика по кнопке (мгновенно), событие `atc:call` от АТС (в реальном времени)
// и GET /atc/active (восстановление после перезагрузки страницы).
export default function OutgoingCallPanel() {
  const t = useT();
  const navigate = useNavigate();
  const me = useSelector(s => s.auth.user);
  const meId = String(me?._id || me?.id || '');
  const myExt = me?.atcExtension || '';
  const [call, setCall] = useState(null);       // { phase, phone, name, contactId, ext, callId, startedAt, answeredAt, duration, reason, origin }
  const [now, setNow] = useState(Date.now());
  const [retrying, setRetrying] = useState(false);
  const dismissTimer = useRef(null);

  const fromPayload = useCallback((p, prev) => ({
    ...(prev && last9(prev.phone) === last9(p.phone) ? prev : {}),
    phase: p.status === 'ringing' ? 'ringing' : p.status === 'active' ? 'active' : (p.result === 'completed' ? 'completed' : 'missed'),
    phone: p.phone, name: p.contactName || (prev && last9(prev.phone) === last9(p.phone) ? prev.name : '') || '', contactId: p.contactId || null,
    ext: p.ext, callId: p.callId, origin: p.origin, reason: p.reason || '', duration: p.duration || 0,
    startedAt: (prev && last9(prev.phone) === last9(p.phone) && prev.startedAt) || (p.startedAt ? new Date(p.startedAt).getTime() : Date.now()),
    answeredAt: p.answeredAt ? new Date(p.answeredAt).getTime() : null,
  }), []);

  // Сразу после клика (локально)
  useEffect(() => {
    const h = (e) => { const d = e.detail || {}; clearTimeout(dismissTimer.current); setCall({ phase: 'initiated', phone: d.phone, name: d.name || '', ext: d.ext || myExt, startedAt: Date.now(), origin: d.via === 'softphone' ? 'direct' : 'crm', via: d.via || 'pbx' }); };
    window.addEventListener('miko:call-started', h);
    return () => window.removeEventListener('miko:call-started', h);
  }, [myExt]);

  // События АТС в реальном времени
  useEffect(() => {
    if (!meId) return undefined;
    const socket = getSocket();
    const on = (p) => {
      if (!p || p.direction !== 'out' || String(p.userId || '') !== meId) return;
      clearTimeout(dismissTimer.current);
      setCall(prev => fromPayload(p, prev));
    };
    socket.on('atc:call', on);
    return () => socket.off('atc:call', on);
  }, [meId, fromPayload]);

  // Восстановление после перезагрузки страницы
  useEffect(() => {
    if (!myExt) return;
    axios.get(`${API}/atc/active`).then(r => { if (r.data.call) setCall(prev => prev || fromPayload(r.data.call, null)); }).catch(() => {});
  }, [myExt, fromPayload]);

  // Таймер
  const live = call && ['initiated', 'ringing', 'active'].includes(call.phase);
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  // Автозакрытие: удачный звонок — через 12 с, неудачный — через 45 с
  useEffect(() => {
    clearTimeout(dismissTimer.current);
    if (call?.phase === 'completed') dismissTimer.current = setTimeout(() => setCall(null), 12000);
    if (call?.phase === 'missed') dismissTimer.current = setTimeout(() => setCall(null), 45000);
    return () => clearTimeout(dismissTimer.current);
  }, [call?.phase, call?.callId]);

  const retry = async () => {
    if (!call?.phone || retrying) return;
    setRetrying(true);
    try {
      if (call.via === 'softphone') {            // повтор через софтфон: снова открываем tel:-ссылку
        window.location.href = `tel:${call.phone}`;
        setCall({ phase: 'initiated', phone: call.phone, name: call.name, ext: myExt, startedAt: Date.now(), origin: 'direct', via: 'softphone' });
        return;
      }
      await axios.post(`${API}/atc/call`, { phone: call.phone });
      setCall({ phase: 'initiated', phone: call.phone, name: call.name, ext: myExt, startedAt: Date.now(), origin: 'crm' });
    } catch (e) { toast.error(e.response?.data?.code === 'no_extension' ? t('callPanel.noExt') : (e.response?.data?.message || t('callPanel.startFailed'))); }
    finally { setRetrying(false); }
  };

  if (!call) return null;
  const who = call.name ? srv(call.name) : fmtPhone(call.phone);
  const waitingTooLong = call.phase === 'initiated' && now - call.startedAt > NO_START_MS;
  const elapsed = Math.floor((now - (call.phase === 'active' && call.answeredAt ? call.answeredAt : call.startedAt)) / 1000);
  const ext = call.ext || myExt;

  const tone = call.phase === 'active' ? 'emerald' : call.phase === 'completed' ? 'emerald' : call.phase === 'missed' ? 'red' : waitingTooLong ? 'amber' : 'primary';
  const toneCls = { emerald: 'bg-emerald-500', red: 'bg-red-500', amber: 'bg-amber-500', primary: 'bg-primary-600' }[tone];
  const Icon = call.phase === 'missed' ? PhoneOff : call.phase === 'active' ? PhoneForwarded : waitingTooLong ? AlertTriangle : PhoneCall;

  let line;
  if (call.phase === 'initiated') line = call.via === 'softphone' ? (waitingTooLong ? t('callPanel.noStartSoftphone', { ext }) : t('callPanel.initiatedSoftphone')) : (waitingTooLong ? t('callPanel.noStart', { ext }) : t('callPanel.initiated'));
  else if (call.phase === 'ringing') line = call.origin === 'direct' ? t('callPanel.ringingDirect') : t('callPanel.ringing', { ext });
  else if (call.phase === 'active') line = t('callPanel.active');
  else if (call.phase === 'completed') line = `${t('callPanel.completed')} · ${t('callPanel.duration', { d: clock(call.duration) })}`;
  else line = `${t('callPanel.missed')}: ${t('callPanel.reason.' + (call.reason || 'unknown'))}`;

  return (
    <div className="fixed z-[90] bottom-20 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[24rem] pointer-events-none">
      <div className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-surface-200 overflow-hidden" role="status" aria-live="polite">
        <div className={`${toneCls} px-4 py-3 flex items-center gap-3 text-white`}>
          <span className="relative w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Icon className={`w-4.5 h-4.5 ${['initiated', 'ringing'].includes(call.phase) && !waitingTooLong ? 'animate-pulse' : ''}`} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-white/80">{t('loc.incomingCallModal.outgoingCall')}</p>
            <p className="text-sm font-semibold truncate">{who}</p>
            {call.name && <p className="text-xs text-white/80">{fmtPhone(call.phone)}</p>}
          </div>
          {(live) && <span className="font-mono text-sm text-white/90 shrink-0">{clock(elapsed)}</span>}
          <button type="button" onClick={() => setCall(null)} aria-label={t('callPanel.close')} className="p-1 rounded-lg hover:bg-white/20 shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <p className={`text-sm leading-snug ${waitingTooLong ? 'text-amber-700' : call.phase === 'missed' ? 'text-red-600' : 'text-ink'}`}>
            {['initiated', 'ringing'].includes(call.phase) && !waitingTooLong && <Loader2 className="inline w-3.5 h-3.5 mr-1.5 animate-spin text-primary-500 align-[-2px]" />}{line}
          </p>
          {(call.phase === 'missed' || call.phase === 'completed' || waitingTooLong) && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={retry} disabled={retrying} className="btn-primary btn-sm inline-flex items-center gap-1.5">
                {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}{t('callPanel.retry')}
              </button>
              {call.contactId && <button type="button" onClick={() => { navigate(`/contacts/${call.contactId}`); setCall(null); }} className="btn-secondary btn-sm inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" />{t('callPanel.openCard')}</button>}
            </div>
          )}
          {call.phase === 'active' && call.contactId && (
            <button type="button" onClick={() => navigate(`/contacts/${call.contactId}`)} className="btn-secondary btn-sm inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" />{t('callPanel.openCard')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
